import { useTheme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Dropdown from '../common/Dropdown';
import FormField from '../common/FormField';
import InputField from '../common/InputField';
import Modal from '../common/Modal';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useGetCompaniesMakersListQuery } from '../../store/apiSlices/companies/companiesMakers/companiesMakersApis';
import { useGetLubricantCategoriesListQuery } from '../../store/apiSlices/lubricants/lubricantCategory/lubricantCategoryApis';
import { useGetLubricantTypesListQuery } from '../../store/apiSlices/lubricants/lubricantTypes/lubricantTypesApis';
import { useGetUnitsOfMeasurementQuery } from '../../store/apiSlices/lubricants/lubricantProducts/lubricantProductsApis';

export interface Step3FormValues {
  productName: string;
  maker: number | string;
  category: number | string;
  type: number | string;
  unit: number | string;
  status: string;
}

interface ProductAddFlowStep3Props {
  onBack: () => void;
  onClose: () => void;
  onSubmit: () => void;
  control: Control<Step3FormValues>;
  errors: FieldErrors<Step3FormValues>;
  isFormValid?: boolean;
}

const ProductAddFlowStep3: React.FC<ProductAddFlowStep3Props> = ({
  onBack,
  onClose,
  onSubmit,
  control,
  errors,
  isFormValid = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Dropdown data APIs for step 3
  const { data: makersData } = useGetCompaniesMakersListQuery(
    {},
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    },
  );
  const { data: categoriesData } = useGetLubricantCategoriesListQuery(
    {},
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    },
  );
  const { data: typesData } = useGetLubricantTypesListQuery(
    {},
    {
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      refetchOnReconnect: true,
    },
  );
  const { data: unitsData } = useGetUnitsOfMeasurementQuery(undefined, {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true,
    refetchOnReconnect: true,
  });

  // Transform dropdown data to { value: id, label: name }
  const makersOptions = useMemo(() => {
    if (!makersData) return [];
    return makersData.map((maker: any) => ({ value: maker.id, label: maker.shortName }));
  }, [makersData]);

  const categoriesOptions = useMemo(() => {
    if (!categoriesData?.data) return [];
    return categoriesData.data.map((category: any) => ({
      value: category.id,
      label: category.categoryName,
    }));
  }, [categoriesData]);

  const typesOptions = useMemo(() => {
    if (!typesData?.data) return [];
    return typesData.data.map((type: any) => ({ value: type.id, label: type.name }));
  }, [typesData]);

  const unitsOptions = useMemo(() => {
    if (!unitsData?.data) return [];
    return unitsData.data
      .filter((unit: any) => ['kg', 'L'].includes(unit.symbol))
      .map((unit: any) => ({ value: unit.id, label: unit.symbol }));
  }, [unitsData]);

  return (
    <Modal
      size="vertical"
      open={true}
      onClose={onClose}
      title={t('productAddFlow.step3Title')}
      isTypeInModal={true}
      footer={
        <>
          <Button variant="red" sx={{ fontSize: '14px' }} onClick={onBack}>
            {t('buttons.back')}
          </Button>
          <Button
            variant="outlinedBlue"
            sx={{ fontSize: '14px' }}
            onClick={onSubmit}
            disabled={!isFormValid}
          >
            {isFormValid
              ? t('productAddFlow.addTypeInAndContinue')
              : t('productAddFlow.fillAllFields')}
          </Button>
        </>
      }
    >
      <Box>
        <Box sx={{ mt: 4, mb: 3, ml: 1 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              color: theme.palette.productAddFlow.heading,
              mb: 1,
            }}
          >
            {t('productAddFlow.typeInTitle')}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              color: theme.palette.productAddFlow.subheading,
              fontSize: '15px',
            }}
          >
            {t('productAddFlow.typeInDescPart1')}
            <br />
            {t('productAddFlow.typeInDescPart2')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 1 }}>
          <FormField label={t('form.productName')} required>
            <Controller
              name="productName"
              control={control}
              rules={{ required: t('form.validation.productNameRequired') }}
              render={({ field }) => (
                <InputField
                  {...field}
                  value={field.value || ''}
                  placeholder={t('form.enterProductName')}
                  error={!!errors?.productName}
                  helperText={errors?.productName?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.maker')} required>
            <Controller
              name="maker"
              control={control}
              rules={{ required: t('form.validation.makerRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={makersOptions}
                  placeholder={t('form.selectMaker')}
                  error={!!errors?.maker}
                  helperText={errors?.maker?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.category')} required>
            <Controller
              name="category"
              control={control}
              rules={{ required: t('form.validation.categoryRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={categoriesOptions}
                  placeholder={t('form.selectCategory')}
                  error={!!errors?.category}
                  helperText={errors?.category?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.type')}>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={typesOptions}
                  placeholder={t('form.selectType')}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.unit')} required>
            <Controller
              name="unit"
              control={control}
              rules={{ required: t('form.validation.unitRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={unitsOptions}
                  placeholder={t('form.selectUnit')}
                  error={!!errors?.unit}
                  helperText={errors?.unit?.message as string}
                />
              )}
            />
          </FormField>

          <FormField label={t('form.status')} required>
            <Controller
              name="status"
              control={control}
              rules={{ required: t('form.validation.statusRequired') }}
              render={({ field }) => (
                <Dropdown
                  value={field.value ?? ''}
                  onChange={(e: any) => field.onChange(e.target.value)}
                  options={[{ value: 'Active', label: t('common.active') }]}
                  hasSearch={false}
                  disabled={true}
                />
              )}
            />
          </FormField>
        </Box>
      </Box>
    </Modal>
  );
};

export default ProductAddFlowStep3;
